import { useEffect } from 'react';
import { useTourContext } from '@/store/tourContext';
import { tutorialActions } from '@/utils/tutorials';
import { useTutorialActive } from './useTutorialActive';

export interface UseReturnsActionsParams {
	openCreateReturnModal: () => void;
	goToReturnStep?: (step: number) => void;
	onCloseDemoReturnModal?: () => void;
}

/**
 * Hook to register tutorial actions for Returns tutorial
 */
export const useReturnsActions = ({
	openCreateReturnModal,
	goToReturnStep,
	onCloseDemoReturnModal,
}: UseReturnsActionsParams) => {
	const { isReturnsTutorialActive: isReturnsTutorialWCActive, isOnboardingActive } =
		useTutorialActive();
	const { activeTutorialName } = useTourContext();
	
	const isAnyReturnsTutorialActive = 
		isReturnsTutorialWCActive ||
		(isOnboardingActive && 
			(activeTutorialName === 'OnboardingMindsightTutorial' ||
				activeTutorialName === 'OnboardingWCTutorial' ||
				activeTutorialName === 'OnboardingSalesRepTutorial'));
	useEffect(() => {
		// Action to open demo return modal
		tutorialActions.register('openCreateReturnModal', () => {

			openCreateReturnModal();
			if (goToReturnStep) {
				goToReturnStep(1);
			}
		});

		// Helper function to register step actions
		const registerStepAction = (name: string, step: number) => {
			tutorialActions.register(name, () => {
				openCreateReturnModal();
				if (goToReturnStep) {
					requestAnimationFrame(() => {
						setTimeout(() => {
							goToReturnStep(step);
						}, 150);
					});
				}
			});
		};

		// Actions for navigating between form steps
		registerStepAction('clickStep2ReturnForm', 2);
		registerStepAction('clickStep3ReturnForm', 3);
		registerStepAction('openCreateReturnModalStep1', 1);
		registerStepAction('openCreateReturnModalStep2', 2);
		registerStepAction('openCreateReturnModalStep3', 3);

		// Actions for highlighting specific fields in the form
		registerStepAction('highlightTissueId', 3);
		registerStepAction('highlightExpirationDate', 3);
		registerStepAction('highlightDateReturned', 3);
		registerStepAction('highlightShippingDate', 3);
		registerStepAction('highlightSubmitReturn', 3);

		// Action to close create return modal
		tutorialActions.register('closeCreateReturnModal', () => {
			if (onCloseDemoReturnModal) {
				onCloseDemoReturnModal();
			}
		});

		// Note: expandActionColumn is handled by useReturnsTutorial hook
		// Do not register it here to avoid overriding the implementation

		return () => {
			tutorialActions.unregister('openCreateReturnModal');
			tutorialActions.unregister('clickStep2ReturnForm');
			tutorialActions.unregister('clickStep3ReturnForm');
			tutorialActions.unregister('openCreateReturnModalStep1');
			tutorialActions.unregister('openCreateReturnModalStep2');
			tutorialActions.unregister('openCreateReturnModalStep3');
			tutorialActions.unregister('highlightTissueId');
			tutorialActions.unregister('highlightExpirationDate');
			tutorialActions.unregister('highlightDateReturned');
			tutorialActions.unregister('highlightShippingDate');
			tutorialActions.unregister('highlightSubmitReturn');
			tutorialActions.unregister('closeCreateReturnModal');
			// Note: expandActionColumn is unregistered by useReturnsTutorial hook
		};
	}, [
		openCreateReturnModal,
		goToReturnStep,
		onCloseDemoReturnModal,
		isAnyReturnsTutorialActive,
	]);
};

